import React, { useContext, useEffect, useMemo, useState } from 'react';
import styles from './AddCategoryForm.module.scss';
import { CloseRoundSmall, Input, Magnifier } from 'esa-ui-library';
import { Form } from '../../../components';
import { searchNestedTree, useStore } from '../../../../common';
import { PropertyType, TreeValueType } from 'esa-ui-library/lib/types';
import { AuthContext } from '../../../../logic';
import treeStyles from 'esa-ui-library/lib/components/TreeSelect/TreeSelect.module.scss';
import { TreeSelectOptions } from 'esa-ui-library/lib/components/TreeSelectOptions/TreeSelectOptions';
import { Chips } from 'esa-ui-library/lib/components/Chips/Chips';
import { ClickDetector } from 'esa-ui-library/lib/components/ClickDetector/ClickDetector';
import { ArrowDown } from 'esa-ui-library/lib/assets/icons';

interface AssignPartnerFormProps {
	onValidityChange?: (isValid: boolean) => void;
}

const AssignPartnersForm = ({ onValidityChange }: AssignPartnerFormProps): JSX.Element => {
	const [state, dispatch] = useStore();
	const [isLoading, setIsLoading] = useState(true);
	const { token } = useContext(AuthContext);
	const { success, selectedGame, partners, activePartners, gameData } = state;
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [opened, setOpened] = useState<string[]>([]);
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const getGame = async () => {
		setIsLoading(true);
		await dispatch('GAME_INIT', { token, id: selectedGame.internal_game_id });
		setIsLoading(false);
	};

	const onPartnersChange = (val: PropertyType[]) => {
		dispatch('PARTNERS_UPDATE', val);
		dispatch('FORM_GAME_PARTNERS', { activePartners: val });
		onValidityChange?.(Array.isArray(val) && val.length > 0);
	};

	useEffect(() => {
		if (selectedGame) {
			getGame();
		}
	}, [selectedGame?.internal_game_id]);

	useEffect(() => {
		if (Array.isArray(gameData?.assigned_partners) && gameData?.assigned_partners.length > 0 && selectedGame) {
			const preselected = gameData.assigned_partners.map((p: any) => ({
				label: p.name,
				value: p.gid,
				skinId: p.partner_id,
			}));

			dispatch('PARTNERS_UPDATE', preselected);
			dispatch('FORM_GAME_PARTNERS', { activePartners: preselected });
			onValidityChange?.(true);
		} else if (selectedGame) {
			dispatch('PARTNERS_UPDATE', []);
			dispatch('FORM_GAME_PARTNERS', { activePartners: [] });
			onValidityChange?.(false);
		}
	}, [gameData?.assigned_partners, selectedGame]);

	const filteredPartners = useMemo(() => {
		if (!searchTerm) return partners;
		return searchNestedTree(partners, searchTerm);
	}, [partners, searchTerm]);

	const renderOptions = useMemo(
		() => (
			<section className={treeStyles.OptionWrapper}>
				<div className={styles.SearchContainer}>
					<Input
						type='text'
						value={searchTerm}
						onChange={setSearchTerm}
						icon={<Magnifier />}
						placeholder='Search partners'
						small
					/>
					{searchTerm && <CloseRoundSmall className={styles.Clear} onClick={() => setSearchTerm('')} />}
				</div>
				<div className={styles.List}>
					<TreeSelectOptions
						options={filteredPartners}
						selected={activePartners}
						opened={opened}
						onChange={onPartnersChange}
						onOpenedChange={setOpened}
					/>
					{filteredPartners?.length === 0 && <p className={styles.EmptyList}>No results</p>}
				</div>
			</section>
		),
		[filteredPartners, activePartners, opened, searchTerm, onPartnersChange]
	);

	return (
		<section className={isLoading ? styles.DisableForm : ''}>
			<Form success={success}>
				<div>
					<div className={`${treeStyles.TreeSelectContainer} ${styles.PartnerSelect}`}>
						<label className={treeStyles.Label}>
							Partners
							<span className={treeStyles.Required}>*</span>
						</label>
						<ClickDetector
							onOutsideClick={() => {
								setIsOpen(false);
								setSearchTerm('');
							}}
							dataId='detector'
						>
							<div
								className={treeStyles.TreeSelectWrapper}
								onClick={() => !isOpen && setIsOpen(true)}
								data-testid='partners-field'
							>
								<div className={`${treeStyles.TreeSelect} ${isOpen ? treeStyles.Active : ''}`}>
									{!!activePartners?.length && (
										<Chips
											options={activePartners as TreeValueType[]}
											onChange={onPartnersChange}
											removeable
											primary
											limit={1}
										/>
									)}
									{!activePartners?.length && <span className={treeStyles.Placeholder}>Select partners</span>}
									<i className={treeStyles.Icon}>
										<ArrowDown />
									</i>
								</div>
								{isOpen && (
									<div className={treeStyles.OptionContainer} data-testid='options'>
										{renderOptions}
									</div>
								)}
							</div>
						</ClickDetector>
					</div>
				</div>
			</Form>
		</section>
	);
};

export { AssignPartnersForm };
